import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthContext';
import {
  Briefcase, Users, Calendar, Plus, ChevronRight,
  MessageSquare, Wallet, TrendingUp, Clock, ArrowUpRight
} from 'lucide-react';
import { format } from 'date-fns';

export default function DashboardHome() {
  const { activeUserId } = useAuth();
  const [profile, setProfile] = useState<any>(null);
  const [stats, setStats] = useState({ campaigns: 0, applicants: 0, interviews: 0, tokens: 0 });
  const [recentCampaigns, setRecentCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (activeUserId) fetchAll();
  }, [activeUserId]);

  const fetchAll = async () => {
    try {
      const { data: rec } = await supabase
        .from('recruiters')
        .select('*')
        .eq('id', activeUserId!)
        .single();
      setProfile(rec);

      const { data: camps } = await supabase
        .from('campaigns')
        .select('id, title, country, company_name, status, created_at')
        .eq('recruiter_id', activeUserId!)
        .order('created_at', { ascending: false });

      const campaignsList = camps || [];
      setRecentCampaigns(campaignsList.slice(0, 4));

      const campaignIds = campaignsList.map(c => c.id);
      let totalApplicants = 0;
      if (campaignIds.length > 0) {
        const { data: jobs } = await supabase
          .from('jobs')
          .select('id')
          .in('campaign_id', campaignIds);
        const jobIds = (jobs || []).map(j => j.id);
        if (jobIds.length > 0) {
          const { count } = await supabase
            .from('applications')
            .select('id', { count: 'exact', head: true })
            .in('job_id', jobIds);
          totalApplicants = count || 0;
        }
      }

      const { data: interviews } = await supabase
        .from('interviews')
        .select('id, campaign:campaigns!inner(recruiter_id)')
        .eq('campaign.recruiter_id', activeUserId!);

      setStats({
        campaigns: campaignsList.filter(c => c.status === 'active').length,
        applicants: totalApplicants,
        interviews: (interviews || []).length,
        tokens: rec?.tokens_balance || 0,
      });
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  const statusColor: Record<string, string> = {
    active: 'bg-emerald-50 text-emerald-700',
    draft: 'bg-slate-100 text-slate-500',
    completed: 'bg-blue-50 text-blue-700',
    selection_done: 'bg-amber-50 text-amber-700',
    paused: 'bg-red-50 text-red-600',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-[3px] border-slate-200 border-t-slate-800 rounded-full" />
      </div>
    );
  }

  const statCards = [
    { label: 'Active Campaigns', value: stats.campaigns, icon: Briefcase, color: 'text-blue-600', bg: 'bg-blue-50', link: '/app/campaigns' },
    { label: 'Total Applicants', value: stats.applicants, icon: Users, color: 'text-emerald-600', bg: 'bg-emerald-50', link: '/app/candidates' },
    { label: 'Interviews', value: stats.interviews, icon: Calendar, color: 'text-violet-600', bg: 'bg-violet-50', link: '/app/interviews' },
    { label: 'Token Balance', value: stats.tokens, icon: Wallet, color: 'text-amber-600', bg: 'bg-amber-50', link: '/app/wallet' },
  ];

  const quickActions = [
    { label: 'New Campaign', icon: Plus, path: '/app/campaigns/new', primary: true },
    { label: 'Schedule Interview', icon: Calendar, path: '/app/interviews' },
    { label: 'Messages', icon: MessageSquare, path: '/app/messages' },
    { label: 'Reports', icon: TrendingUp, path: '/app/reports' },
  ];

  return (
    <div className="space-y-8 animate-in fade-in" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>

      {/* Greeting */}
      <div>
        <h1 className="text-3xl font-black text-slate-900 tracking-tight">
          {greeting()}, {profile?.name?.split(' ')[0] || 'Recruiter'}
        </h1>
        <p className="text-slate-400 mt-1 text-[15px]">Here's your hiring overview.</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card, i) => {
          const Icon = card.icon;
          return (
            <Link
              key={i}
              to={card.link}
              className="group bg-white rounded-2xl p-5 border border-slate-200/60 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className={`w-10 h-10 ${card.bg} rounded-xl flex items-center justify-center ${card.color} mb-3 group-hover:scale-110 transition-transform`}>
                <Icon size={20} />
              </div>
              <p className="text-2xl font-black text-slate-900 tracking-tight">{card.value.toLocaleString()}</p>
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-400 mt-1">{card.label}</p>
            </Link>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {quickActions.map((action, i) => {
            const Icon = action.icon;
            return (
              <Link
                key={i}
                to={action.path}
                className={`group flex flex-col items-center gap-3 p-5 rounded-2xl transition-all hover:-translate-y-0.5 ${action.primary
                    ? 'bg-slate-950 text-white hover:bg-slate-800 shadow-lg'
                    : 'bg-white text-slate-700 border border-slate-200/60 shadow-sm hover:shadow-md'
                  }`}
              >
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform ${action.primary ? 'bg-white/15' : 'bg-slate-50'
                  }`}>
                  <Icon size={20} className={action.primary ? 'text-white' : 'text-slate-500'} />
                </div>
                <span className="text-[12px] font-bold">{action.label}</span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Recent Campaigns */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">Recent Campaigns</h2>
          <Link to="/app/campaigns" className="text-[12px] font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-colors">
            View All <ArrowUpRight size={12} />
          </Link>
        </div>

        {recentCampaigns.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-slate-200/60 shadow-sm">
            <Briefcase size={40} className="mx-auto text-slate-200 mb-3" />
            <p className="text-[15px] font-semibold text-slate-700">No campaigns yet</p>
            <Link to="/app/campaigns/new" className="text-blue-600 font-bold text-[13px] mt-2 inline-block hover:underline">
              Create your first campaign →
            </Link>
          </div>
        ) : (
          <div className="space-y-2">
            {recentCampaigns.map(camp => (
              <Link
                key={camp.id}
                to={`/app/campaigns/${camp.id}`}
                className="group flex items-center bg-white rounded-2xl px-5 py-4 border border-slate-200/60 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                <div className="w-9 h-9 bg-slate-100 rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
                  <Briefcase size={16} className="text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-slate-800 group-hover:text-blue-600 transition-colors truncate">{camp.title}</p>
                  <p className="text-[11px] text-slate-400">{format(new Date(camp.created_at), 'MMM d, yyyy')} · {camp.country}</p>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase mr-3 ${statusColor[camp.status] || statusColor.draft}`}>
                  {camp.status?.replace('_', ' ')}
                </span>
                <ChevronRight size={16} className="text-slate-300 group-hover:text-blue-500 transition-colors" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
